'use strict';
import Logger from '../global/Logger';
import StringHelper from '../global/StringHelper';
import Icons from '../siteSearch/components/Icons';
import UiHelper from '../siteSearch/ui/UiHelper';
import MediaQueryMatchType from '../sxQuery/MediaQueryMatchType';
import sxQuery from '../sxQuery/sxQuery';
import SxQueryObject from '../sxQuery/SxQueryObject';
import SxQueryUtils from '../sxQuery/SxQueryUtils';
import SuggestionBlock from './ui/SuggestionBlock';
import DataLoader from './utils/DataLoader';
import Helper from './utils/Helper';
import SiteSearchHelper from '../siteSearch/utils/Helper';
import FocusLayerStyle from '../siteSearch/enums/FocusLayerStyle';
import Plan from '../siteSearch/enums/Plan';
import TemplateRenderer from '../global/TemplateRenderer';
import ContentGroupHelper from '../global/ContentGroupHelper';
import DomElementBuilder from '../sxQuery/DomElementBuilder';
const BRANDED_PLANS = [Plan.Free, Plan.DudaFree, Plan.FirespringFree];
var State;
(function (State) {
    State["Empty"] = "empty";
    State["QueryBased"] = "queryBased";
})(State || (State = {}));
const isEnter = (keyCode) => keyCode === 13;
const isEsc = (keyCode) => keyCode === 27;
const isArrowKey = (keyCode) => keyCode >= 37 && keyCode <= 40;
const getArrowKeyDirection = (keyCode) => {
    if (keyCode === 38) {
        return -1;
    }
    if (keyCode === 40) {
        return 1;
    }
    return null;
};
const LAYOUT_TYPES = [
    1, // column
    2, // two columns, narrow column left
    3, // two columns, equal size
    4 // two columns, narrow column right
];
var UniboxMode;
(function (UniboxMode) {
    UniboxMode[UniboxMode["Default"] = 0] = "Default";
    UniboxMode[UniboxMode["PopOver"] = 1] = "PopOver";
})(UniboxMode || (UniboxMode = {}));
const createSearchSection = (context) => {
    const pluginConfiguration = context.pluginConfiguration;
    const searchBoxId = `unibox-custom-search-box-${Math.round(Math.random() * 10000)}`;
    const section = sxQuery('<section role="search" class="z-search-flex z-search-n-section z-search-custom-search z-search-custom-search--no-margin unibox__custom-search"></section>');
    const searchFieldLabel = pluginConfiguration.accessibility.searchFieldLabel;
    const placeholder = pluginConfiguration.searchBox.placeholder || '';
    const searchBox = sxQuery(`<input id="${searchBoxId}" autocomplete="off" type="search" class="z-search-custom-search__searchbox z-search-n-input" placeholder="${placeholder}">`);
    if (searchFieldLabel !== undefined) {
        const ss360Label = sxQuery(`<label style="${SxQueryUtils.srOnlyCss}" for="${searchBoxId}" class="z-search-sr-only">${searchFieldLabel}</label>`);
        section.append(ss360Label);
    }
    const ss360SearchButton = sxQuery('<button type="button" class="z-search-custom-search__searchbutton z-search-n-button"></button>');
    const searchBoxConfig = context.pluginConfiguration.searchBox;
    if (searchBoxConfig.searchButtonLabel !== undefined) {
        ss360SearchButton.addClass('z-search-custom-search__searchbutton--text');
        ss360SearchButton.html(searchBoxConfig.searchButtonLabel);
    }
    else {
        ss360SearchButton.html(Icons.getSvgIcon(Icons.MAGNIFIER, UiHelper.getSearchButtonIconColor(context.pluginConfiguration)));
        ss360SearchButton.attr('aria-label', 'Search');
    }
    section.append(searchBox);
    section.append(ss360SearchButton);
    return section;
};
class UniBox {
    constructor(searchBox, context, hasMultipleSearchBoxes, trigger) {
        this.emptyContainerOnShow = false;
        const { pluginConfiguration } = context;
        if (searchBox === undefined && trigger !== undefined) {
            this.mode = UniboxMode.PopOver;
            this.trigger = trigger;
            const section = createSearchSection(context);
            searchBox = section.find('input[type="search"]');
            trigger.on('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!this.isVisible && !this.isHiding) {
                    this.moveToSearchBoxContext(e);
                    this.update();
                }
            });
            this.customSearchBoxSection = section;
        }
        else {
            this.mode = UniboxMode.Default;
        }
        this.searchBox = searchBox;
        this.context = context;
        this.id = this.mode === UniboxMode.Default ? context.uniboxId : `${context.uniboxId}--layover`;
        this.hasMultipleSearchBoxes = hasMultipleSearchBoxes;
        this.selectedEntryIndex = -1;
        // @ts-ignore
        this.lastVal = searchBox.val();
        this.suggestUrl = '';
        this.cache = {};
        this.isVisible = false;
        this.totalLength = 0;
        this.keep = false;
        this.preventFocusHandler = false;
        this.isHiding = false;
        this.hasBeenShown = false;
        this.renderPrevented = false;
        this.preventSearchEvent = false;
        // get container
        if (pluginConfiguration.suggestions.searchBoxContainerSelector === undefined) {
            this.searchBoxParent = this.mode === UniboxMode.Default ? searchBox.parent() : trigger.parent();
        }
        else {
            this.searchBoxParent = sxQuery(pluginConfiguration.suggestions.searchBoxContainerSelector);
            this.emptyContainerOnShow = pluginConfiguration.suggestions.emptyContainerOnShow;
        }
        // get search button
        if (pluginConfiguration.searchBox.searchButton !== undefined) {
            const searchButtons = this.searchBoxParent.find(pluginConfiguration.searchBox.searchButton);
            if (searchButtons.length === 1) {
                if (Math.abs(searchButtons.offset().top - searchBox.offset().top) < 5
                    && searchButtons.offset().left > searchBox.offset().left) { // similar y level and search button to the right of the search box
                    this.searchButton = searchButtons;
                }
            }
        }
        // get ids
        this.uniboxControlsId = context.uniboxControlsId;
        this.uniboxStatusMessageId = context.uniboxStatusMessageId;
        this.viewKeyMappings = context.pluginConfiguration.suggestions.viewKeyMappings || {};
        if (this.viewKeyMappings.ss360QuerySuggestions === undefined) {
            this.viewKeyMappings.ss360QuerySuggestions = context.pluginConfiguration.suggestions.querySuggestionHeadline || '';
        }
        if (this.viewKeyMappings._ === undefined) {
            this.viewKeyMappings._ = context.pluginConfiguration.contentGroups.otherName || '';
        }
        if (this.viewKeyMappings['*'] === undefined) {
            this.viewKeyMappings['*'] = context.pluginConfiguration.suggestions.allResultsName;
        }
    }
    init() {
        this.loadStyles();
        this.updateSearchBoxAttributes();
        this.appendSuggestBox();
        this.searchBox.off('focus.unibox, focusout.unibox, keydown.unibox, keyup.unibox, search.unibox');
        this.searchBox.on('keyup.unibox', (e) => {
            if (e === undefined || e === null) {
                // @ts-ignore
                e = window.event;
            }
            this.handleKeyUp(e);
        }).on('keydown.unibox', (e) => {
            if (e === undefined || e === null) {
                // @ts-ignore
                e = window.event;
            }
            this.handleKeyDown(e);
        }).on('focus.unibox', (e) => {
            // @ts-ignore
            if (e.preventSuggests === true || this.preventFocusHandler || this.mode === UniboxMode.PopOver) {
                this.preventFocusHandler = false;
                return;
            }
            if (this.isFullWidth()) {
                this.suggestBox.addClass('unibox--fullwidth');
                this.scrollIntoView();
            }
            else {
                this.suggestBox.removeClass('unibox--fullwidth');
            }
            this.moveToSearchBoxContext(e);
            const contentGroups = ContentGroupHelper.get(sxQuery(e.target), true, false, false, this.context);
            this.includeOverrides = contentGroups.include;
            this.excludeOverrides = contentGroups.exclude;
            this.update();
            const focusCallback = this.context.pluginConfiguration.callbacks.focus;
            if (focusCallback !== undefined) {
                focusCallback(e, this.lastVal);
            }
        }).on('focusout.unibox', () => {
            if (!this.keep && !SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Max, 767)) { // don't hide on blur on mobile (virtual keyboard should not close this)
                setTimeout(() => {
                    if (sxQuery(document.activeElement).parents(`#${this.id}`).length === 0) {
                        this.hide();
                    }
                }, 10);
            }
        }).on('search.unibox', () => {
            if (this.preventSearchEvent) {
                return;
            }
            // @ts-ignore
            this.lastVal = this.searchBox.val();
            if (this.searchBox.val().length > 0) {
                this.selectedEntryIndex = -1;
                this.handleEnter();
            }
            else {
                this.throttle(() => {
                    this.update();
                });
            }
        });
        let isResizing = false;
        sxQuery(window).on('resize', () => {
            if (isResizing)
                return;
            isResizing = true;
            SxQueryUtils.requestAnimationFrame(() => {
                this.resizeAndReposition();
                isResizing = false;
            });
        });
        // click outside of suggest div closes it
        sxQuery('html').click((e) => {
            if (!this.isVisible)
                return;
            try {
                const id = e !== undefined ? sxQuery(e.target).attr('id') : undefined;
                if ((id !== undefined && id !== null && (id === this.searchBox.attr('id') || id === this.id)) || sxQuery(e.target).parents(`#${this.id}`).length > 0) { // click within the search box or suggest box
                    return;
                }
                if (e !== undefined && e.target === this.searchBox.get(0)) {
                    return;
                }
            }
            catch (ex) {
                Logger.warn(ex);
            }
            this.hide();
        });
    }
    scrollIntoView() {
        const offsetTop = this.searchBox.offset().top - sxQuery('body').offset().top;
        sxQuery('html, body').animateScrollTop(offsetTop, 2 * this.context.pluginConfiguration.style.animationSpeed);
    }
    moveToSearchBoxContext(e) {
        if (!this.hasMultipleSearchBoxes)
            return;
        // move unibox to current sb context (if multiple search boxes)
        const isUniboxFirstLevelChild = (parentNode) => {
            if (parentNode.children) {
                for (let i = 0; i < parentNode.children.length; i++) {
                    if (parentNode.children[i].getAttribute('id') === this.id) {
                        return true;
                    }
                }
            }
            return false;
        };
        const parent = sxQuery(e.target).parent();
        if (parent.length > 0 && (parent.find(`#${this.id}`).length === 0 || !isUniboxFirstLevelChild(parent.get()[0]))) {
            this.addSuggestBoxToParent(parent, sxQuery(e.target));
        }
    }
    addSuggestBoxToParent(parent, searchBox) {
        if (searchBox === undefined) {
            searchBox = this.searchBox;
        }
        let insertAfter;
        if (parent.length === 1 && this.searchButton && this.searchButton.length === 1 && parent.children().get().indexOf(this.searchButton.get(0)) !== -1) {
            insertAfter = this.searchButton.get(0);
        }
        else if (parent.length === 1 && searchBox && searchBox.length === 1 && parent.children().get().indexOf(searchBox.get(0)) !== -1) {
            insertAfter = searchBox.get(0);
        }
        if (insertAfter !== undefined) {
            if (insertAfter.nextSibling !== null) {
                parent.get(0).insertBefore(this.suggestBox.get(0), insertAfter.nextSibling);
            }
            else {
                parent.append(this.suggestBox);
            }
        }
        else {
            parent.prepend(this.suggestBox);
        }
    }
    appendSuggestBox() {
        sxQuery(`#${this.id}`).remove();
        const suggestBox = sxQuery(`<div id="${this.id}" class="unibox unibox-n-section" role="listbox"></div>`);
        suggestBox.attr('aria-label', this.context.pluginConfiguration.suggestions.suggestionsLayerLabel);
        if (this.mode === UniboxMode.PopOver) {
            suggestBox.addClass('unibox--popover');
            suggestBox.append(this.customSearchBoxSection);
        }
        this.suggestBox = suggestBox;
        this.addSuggestBoxToParent(this.searchBoxParent);
        const pos = this.searchBoxParent.css('position');
        if (pos !== 'absolute') {
            this.searchBoxParent.css('position', 'relative');
        }
        // @ts-ignore
        const borderSize = (suggestBox.css('border-width') || '0px').replace('px', '');
        suggestBox.css('min-width', this.searchBox.outerWidth() - 2 * borderSize);
        const maxWidth = this.context.pluginConfiguration.suggestions.maxWidth;
        if (maxWidth !== 'auto') {
            // @ts-ignore
            suggestBox.css('max-width', maxWidth - 2 * borderSize);
        }
        suggestBox.on('mousedown', () => {
            this.keep = true;
        }).on('mouseup', () => {
            setTimeout(() => {
                this.keep = false;
            }, 0);
        });
        this.resizeAndReposition();
    }
    loadStyles() {
        const { pluginConfiguration } = this.context;
        const $target = pluginConfiguration.style.targetNode ? sxQuery(pluginConfiguration.style.targetNode) : sxQuery(document);
        const $appendNode = $target.find('head').length > 0 ? $target.find('head') : $target;
        if ($appendNode.find('#unibox-css').length === 0 && pluginConfiguration.style.defaultCss) {
            // eslint-disable-next-line global-require
            const css = require('../../sass/siteSearch/unibox.scss');
            $appendNode.append(`<style id="unibox-css" type="text/css">${css}</style>`);
            const { accentColor } = pluginConfiguration.style;
            const $bodyAppendNode = $target.find('body').length > 0 ? $target.find('body') : $target;
            if (accentColor !== undefined) {
                const accentColorRgba = SiteSearchHelper.colorToRgb(accentColor);
                if (accentColorRgba !== undefined && accentColorRgba !== null) {
                    const rgba = `rgba(${accentColorRgba.r}, ${accentColorRgba.g}, ${accentColorRgba.b}, 0.19)`;
                    // eslint-disable-next-line max-len
                    $bodyAppendNode.append(`<style id="unibox-highlight">.unibox__selectable--active:not(.unibox__selectable--show-all):not(.unibox__selectable--cta), .unibox__selectable:hover:not(.unibox__selectable--show-all):not(.unibox__selectable--cta){background: ${rgba}}</style>`);
                }
            }
        }
    }
    updateSearchBoxAttributes() {
        const searchBox = this.searchBox;
        // insert necessary values for inputfield
        searchBox.attr('autocomplete', 'off');
        const { pluginConfiguration } = this.context;
        // append invisible label for search field (if no label is defined)
        const { accessibility } = pluginConfiguration;
        const searchFieldLabel = accessibility.searchFieldLabel;
        if (searchFieldLabel && searchBox.attr('aria-label') === null && (!searchBox.attr('id') || sxQuery(`label[for='${searchBox.attr('id')}']`).length === 0)) {
            if (!searchBox.attr('id')) {
                // @ts-ignore
                if (!window.uniboxCounter) {
                    // @ts-ignore
                    window.uniboxCounter = 0;
                }
                // @ts-ignore
                window.uniboxCounter++;
                // @ts-ignore
                searchBox.attr('id', `unibox-search-box-${window.uniboxCounter}`);
            }
            searchBox.attr('aria-label', searchFieldLabel);
        }
        // handling the placeholder
        // if placeholder is not undefined and length > 0 go on, else no placeholder at all
        const { placeholder } = pluginConfiguration.searchBox;
        if (placeholder !== undefined && placeholder.length > 0 && searchBox.data('ss360KeepPlaceholder') !== 'true' && searchBox.data('zoovuKeepPlaceholder') !== 'true') {
            searchBox.attr('placeholder', placeholder);
        }
        if (sxQuery(`#${this.uniboxControlsId}`).length === 0) {
            const controlDescriptionText = accessibility.srSuggestBoxControlDescription;
            const controlDescription = `<span id='${this.uniboxControlsId}' style='${SxQueryUtils.srOnlyCss}' class='unibox-sr-only' tabindex='-1'>${controlDescriptionText}</span>`;
            searchBox.parent().append(controlDescription);
        }
        if (sxQuery(`#${this.uniboxStatusMessageId}`).length === 0) {
            // add aria-live region to announce search results
            const ariaLive = sxQuery(`<span id="${this.uniboxStatusMessageId}" style="${SxQueryUtils.srOnlyCss}" tabindex="-1" aria-live="polite" aria-atomic="true" role="status" class="unibox-sr-only">`);
            searchBox.parent().append(ariaLive);
        }
        searchBox.attr('role', 'combobox');
        searchBox.attr('aria-describedby', this.uniboxControlsId);
        searchBox.attr('aria-owns', this.id);
        searchBox.attr('aria-controls', this.id);
        searchBox.attr('aria-expanded', 'false');
        searchBox.attr('aria-autocomplete', 'list');
        searchBox.attr('aria-haspopup', 'listbox');
    }
    handleKeyUp(event) {
        if (event === undefined) {
            this.hide();
            return;
        }
        const val = this.searchBox.val();
        const typeCallback = this.context.pluginConfiguration.callbacks.type;
        if (typeCallback !== undefined) {
            // @ts-ignore
            typeCallback(event, val);
        }
        if (val !== this.lastVal && (event === undefined || !isArrowKey(this.getKeyCode(event)))) { // check for arrow keys not to retrigger the search after a query suggestion was selected
            // @ts-ignore
            this.lastVal = val;
            if (this.context.hasInsights()) {
                this.context.getInsights().collectSearchBoxChange(this.searchBox.get()[0]);
            }
            this.throttle(() => {
                this.update();
            }, this.cache[val] !== undefined ? 10 : undefined);
        }
    }
    getKeyCode(event) {
        let keyCode = event.keyCode || event.which;
        if (keyCode !== undefined && keyCode === 0 && this.savedKeyCodeOnKeyDown !== undefined) {
            keyCode = this.savedKeyCodeOnKeyDown;
            this.savedKeyCodeOnKeyDown = undefined;
        }
        return keyCode;
    }
    handleKeyDown(event) {
        if (event === undefined) {
            this.hide();
            return;
        }
        const keyCode = this.getKeyCode(event);
        if (isEnter(keyCode) && !this.context.pluginConfiguration.results.ignoreEnter) {
            event.preventDefault();
            event.stopPropagation();
            // clear cache on every search submit, e.g. to reload search history
            this.emptyCache = undefined;
            this.cache = {};
            this.handleEnter();
        }
        else if (isArrowKey(keyCode)) {
            this.handleArrowKey(event, keyCode);
        }
        else if (isEsc(keyCode)) {
            this.preventSearchEvent = true;
            this.hide();
            setTimeout(() => {
                this.preventSearchEvent = false;
            }, 10);
        }
    }
    handleEnter(searchButton, resultGroup) {
        const { core, pluginConfiguration } = this.context;
        if (this.selectedEntryIndex === -1 && !pluginConfiguration.results.ignoreEnter) {
            if (!pluginConfiguration.layout.navigation.keepOpenTab || resultGroup === undefined) {
                core.context.createCookie('-open-tab', '', -1);
            }
            else {
                core.context.createCookie('-open-tab', `${this.lastVal}<#>${StringHelper.getSafeKey(resultGroup)}`, 1 / 24);
            }
            this.hide();
            // @ts-ignore
            pluginConfiguration.callbacks.enter(this.searchBox.val(), undefined, undefined, undefined, searchButton !== undefined ? searchButton.get()[0] : undefined);
        }
        else if (this.selectedEntryIndex !== -1) {
            const totalLength = this.totalLength + (this.showAllButton !== undefined ? 1 : 0);
            if (this.showAllButton !== undefined && this.selectedEntryIndex === totalLength - 1) {
                this.onCtaClicked(this.showAllButton);
            }
            else {
                const target = this.getFocusedBlock(this.selectedEntryIndex);
                if (target.block.isCta(target.selectedIndex)) {
                    this.onCtaClicked(target.block.cta, target.block.getResultGroup());
                }
                else {
                    const selectedItem = target.block.get(target.selectedIndex);
                    if (selectedItem.clickHandler === undefined) {
                        this.onItemSelected(selectedItem, target.block.getSelectable(target.selectedIndex));
                    }
                    else if (selectedItem.clickHandler()) {
                        this.hide();
                    }
                }
            }
        }
    }
    onCtaClicked(searchButton, resultGroup) {
        if (this.lastVal.length === 0) {
            this.lastVal = '*';
            this.searchBox.val('*');
        }
        this.hide();
        const { core, pluginConfiguration } = this.context;
        if (!pluginConfiguration.layout.navigation.keepOpenTab || resultGroup === undefined) {
            core.context.createCookie('-open-tab', '', -1);
        }
        else {
            core.context.createCookie('-open-tab', `${this.lastVal}<#>${StringHelper.getSafeKey(resultGroup)}`, 1 / 24);
        }
        core.showResults(this.searchBox.val() || this.lastVal || '*', { searchButton: searchButton !== undefined ? searchButton.get()[0] : undefined });
    }
    onItemSelected(entry, node, event) {
        const { title, link } = entry;
        const enterResultCallback = this.context.pluginConfiguration.callbacks.enterResult;
        const prevVal = this.lastVal;
        const selectedText = title || prevVal;
        if (link === undefined || link === null || link.trim().length === 0) {
            this.searchBox.val(selectedText);
        }
        enterResultCallback(selectedText, link, event !== undefined ? event.ctrlKey : false, prevVal);
        this.hide();
        if (this.context.hasInsights()) {
            let idx = -1;
            let offset = 0;
            for (let i = 0; i < this.blocks.length; i++) {
                for (let j = 0; j < this.blocks[i].length(); j++) {
                    if (this.blocks[i].get(j) && this.blocks[i].get(j).id === entry.id) {
                        idx = offset;
                        break;
                    }
                    offset++;
                }
                if (idx !== -1) {
                    break;
                }
            }
            const isSearchHistory = (entry.id || '').indexOf('history_') === 0;
            const trackTitle = isSearchHistory || ((prevVal || '').length === 0);
            this.context.getInsights().trackSelectSuggest(this.searchBox.get()[0], this.suggestBox.get()[0], node.get()[0], trackTitle ? title : prevVal, this.totalLength, idx, link, entry.contentGroup, isSearchHistory ? 'history' : undefined, undefined, undefined, this.state === State.Empty);
        }
    }
    suggestionsBelow(positionRoot) {
        const settings = this.context.pluginConfiguration.suggestions;
        if (settings.forceBelow) {
            return true;
        }
        if (settings.mobileScrollOnFocus && SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Min, 767)) {
            return true;
        }
        if (positionRoot === undefined) {
            positionRoot = this.mode === UniboxMode.Default ? this.searchBox : this.trigger;
        }
        const bb = positionRoot.get()[0].getBoundingClientRect();
        const vph = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        const spaceAbove = bb.y || bb.top;
        const spaceBelow = vph - spaceAbove - bb.height;
        return spaceBelow >= spaceAbove;
    }
    handleArrowKey(event, keyCode) {
        const dir = getArrowKeyDirection(keyCode);
        if (dir === null && this.selectedEntryIndex !== -1) { // prevent changing position in search box if suggestion is focused
            event.preventDefault();
            event.stopPropagation();
            this.searchBox.val(this.lastVal);
        }
        else if (dir !== null) {
            event.preventDefault();
            event.stopPropagation();
            const totalLength = this.getTotalLength();
            const lastEntryIdx = this.selectedEntryIndex;
            this.selectedEntryIndex += dir;
            if (this.selectedEntryIndex >= totalLength) {
                this.selectedEntryIndex = -1;
            }
            else if (this.selectedEntryIndex < -1) {
                this.selectedEntryIndex = totalLength - 1;
            }
            // fill in query into search field
            const target = this.getFocusedBlock(this.selectedEntryIndex);
            if (target !== undefined && !target.block.isCta(target.selectedIndex)) {
                const selectedItem = target.block.get(target.selectedIndex);
                const selectedItemLink = selectedItem.link;
                if (selectedItem.title !== undefined && selectedItem.title.length > 0 && (selectedItemLink === undefined || selectedItemLink === null || selectedItemLink.length === 0) && selectedItem.clickHandler === undefined) {
                    this.searchBox.val(selectedItem.title);
                }
                else {
                    this.searchBox.val(this.lastVal);
                }
            }
            else {
                this.searchBox.val(this.lastVal);
            }
            this.changeSelectedItem(lastEntryIdx);
        }
    }
    getTotalLength() {
        return this.totalLength + (this.showAllButton !== undefined ? 1 : 0);
    }
    changeSelectedItem(lastEntryIdx) {
        this.suggestBox.removeAttribute('aria-activedescendant');
        const oldBlock = this.getFocusedBlock(lastEntryIdx);
        if (oldBlock !== undefined) {
            oldBlock.block.removeHighlight();
        }
        if (this.showAllButton !== undefined) {
            Helper.removeHighlight(this.showAllButton);
        }
        if (this.showAllButton !== undefined && this.selectedEntryIndex === this.getTotalLength() - 1) {
            this.suggestBox.attr('aria-activedescendant', Helper.HIGHLIGHT_ID);
            Helper.addHighlight(this.showAllButton);
        }
        else {
            const newBlock = this.getFocusedBlock(this.selectedEntryIndex);
            if (newBlock !== undefined) {
                this.suggestBox.attr('aria-activedescendant', Helper.HIGHLIGHT_ID);
                newBlock.block.highlight(newBlock.selectedIndex);
            }
        }
    }
    getFocusedBlock(entryIdx) {
        let offset = 0;
        if (entryIdx === -1)
            return undefined;
        for (let i = 0; i < (this.blocks || []).length; i++) {
            const block = this.blocks[i];
            if (offset <= entryIdx && offset + block.length() > entryIdx) {
                return {
                    block,
                    selectedIndex: entryIdx - offset
                };
            }
            offset += block.length();
        }
        return undefined;
    }
    show() {
        const suggestBox = this.suggestBox;
        const animationSpeed = this.context.pluginConfiguration.style.animationSpeed;
        const display = !this.isGrid() ? 'block' : 'grid';
        if (!this.hasBeenShown) {
            this.resizeAndReposition();
            this.hasBeenShown = true;
        }
        this.addDarkenLayer();
        if (this.suggestionsBelow() && !this.isVisible) {
            // if suggestbox currently not visible, slide down
            suggestBox.slideDown(animationSpeed, () => {
                suggestBox.addClass('unibox--active');
                // re-position it (in some cases the slide down moves the search box and the suggest box is not aligned anymore)
                this.resizeAndReposition();
                this.isVisible = true;
            }, display);
            if (this.mode === UniboxMode.PopOver) {
                setTimeout(() => {
                    suggestBox.scrollTop(0);
                }, 0);
            }
        }
        else if (!this.isVisible) {
            suggestBox.css('display', display);
            suggestBox.addClass('unibox--active');
            this.resizeAndReposition();
            this.isVisible = true;
        }
        else {
            suggestBox.css('display', display);
            this.resizeAndReposition();
        }
        this.searchBox.attr('aria-expanded', 'true');
        if (this.mode === UniboxMode.PopOver) {
            this.searchBox.focus();
        }
        if (this.searchBoxParent && this.emptyContainerOnShow) {
            this.searchBoxParent.children().each((el) => {
                const $el = sxQuery(el);
                if (!$el.hasClass('unibox')) {
                    $el.remove();
                }
            });
        }
    }
    isFullWidth() {
        return this.context.pluginConfiguration.suggestions.mobileScrollOnFocus && SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Max, 767);
    }
    isGrid() {
        return this.lastLayoutType !== 1 && SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Min, 992);
    }
    resizeAndReposition() {
        const suggestBox = this.suggestBox;
        const positionRoot = this.mode === UniboxMode.Default ? this.searchBox : this.trigger;
        const searchBoxOffset = {
            left: positionRoot.offset().left - this.searchBoxParent.offset().left,
            top: positionRoot.offset().top - this.searchBoxParent.offset().top + this.searchBox.outerHeight()
        };
        if (this.isFullWidth()) {
            suggestBox.css('left', 0);
            suggestBox.css('max-width', '100%');
            suggestBox.css('min-width', '100%');
            suggestBox.css('width', '100%');
            suggestBox.css('top', searchBoxOffset.top);
            return;
        }
        const { maxWidth } = this.context.pluginConfiguration.suggestions;
        // @ts-ignore
        const borderSize = parseInt((suggestBox.css('border-width') || '0px').replace('px', ''), 10);
        const hasAutoWidth = maxWidth === 'auto' || !parseInt(maxWidth.toString(), 10);
        // @ts-ignore
        let minWidth = hasAutoWidth ? positionRoot.outerWidth() - 2 * borderSize : maxWidth - 2 * borderSize;
        if (hasAutoWidth && this.isGrid() && SxQueryUtils.matchesMediaQuery(MediaQueryMatchType.Min, 992)) {
            minWidth = Math.max(minWidth, 800);
        }
        else if (hasAutoWidth && this.mode === UniboxMode.PopOver) {
            minWidth = Math.max(minWidth, 400);
        }
        else if (hasAutoWidth) {
            minWidth = Math.max(minWidth, 250);
        }
        let searchButtonWidth = 0;
        if (this.searchButton !== undefined) {
            // @ts-ignore
            const buttonBorderWidth = parseInt((this.searchButton.css('borderWidth') || '0px').replace('px', ''), 10);
            searchButtonWidth = Math.min(this.searchButton.outerWidth(), 80) - buttonBorderWidth; // substract border just once, one border should be just next to the search field
        }
        let uniboxMaxWidth;
        if (hasAutoWidth) {
            minWidth += searchButtonWidth;
            uniboxMaxWidth = Math.max(275 - 2 * borderSize, positionRoot.outerWidth() - 2 * borderSize + searchButtonWidth);
        }
        else {
            uniboxMaxWidth = parseInt(maxWidth.toString(), 10) - 2 * borderSize;
        }
        uniboxMaxWidth = Math.max(minWidth, uniboxMaxWidth);
        suggestBox.css('min-width', minWidth);
        suggestBox.css('max-width', uniboxMaxWidth);
        if (this.suggestionsLeft(positionRoot, uniboxMaxWidth)) {
            const bb = positionRoot.get()[0].getBoundingClientRect();
            const r = (bb.x || bb.left) + bb.width;
            let l = r - uniboxMaxWidth;
            if (positionRoot.parent().css('position') === 'relative') {
                const pBB = positionRoot.parent().get()[0].getBoundingClientRect();
                l -= (pBB.x || pBB.left);
                // would go out of the client? --> move to the right
                const wo = (pBB.x || pBB.left) + l;
                if (l < 0 && wo < 0) {
                    l -= wo;
                }
            }
            else {
                l = Math.max(0, l);
            }
            suggestBox.css('left', l);
        }
        else {
            const bb = positionRoot.get()[0].getBoundingClientRect();
            const widthDiff = Math.max(uniboxMaxWidth - bb.width, 0);
            suggestBox.css('left', Math.max((searchBoxOffset.left - (this.mode === UniboxMode.PopOver ? widthDiff / 2 : 0)), 0));
        }
        if (this.suggestionsBelow(positionRoot)) {
            suggestBox.css('top', searchBoxOffset.top);
        }
        else {
            suggestBox.css('top', searchBoxOffset.top - suggestBox.outerHeight() - positionRoot.outerHeight());
        }
    }
    // eslint-disable-next-line class-methods-use-this
    suggestionsLeft(positionRoot, sbWidth) {
        const bb = positionRoot.get()[0].getBoundingClientRect();
        const w = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        return ((bb.x || bb.left) + sbWidth) > w;
    }
    hide(e) {
        this.selectedEntryIndex = -1;
        if (!this.isVisible) {
            return;
        }
        // prevent results from being loaded
        clearTimeout(this.timeoutId);
        this.renderPrevented = true;
        this.isHiding = true;
        this.isVisible = false;
        const suggestBox = this.suggestBox;
        this.searchBox.attr('aria-expanded', 'false');
        const statusElement = sxQuery(`#${this.uniboxStatusMessageId}`);
        const { pluginConfiguration } = this.context;
        const contentText = pluginConfiguration.accessibility.srSuggestionsHiddenText;
        if (statusElement.text() !== contentText) {
            statusElement.html(contentText);
        }
        this.searchBox.removeAttribute('aria-activedescendant');
        const blurCallback = pluginConfiguration.callbacks.blur;
        if (blurCallback !== undefined) {
            try {
                blurCallback.call(this, e, this.searchBox.val(), false);
            }
            catch (ex) {
                Logger.warn(ex);
            }
        }
        suggestBox.removeClass('unibox--active');
        suggestBox.slideUp(pluginConfiguration.style.animationSpeed, () => {
            this.isHiding = false;
        });
        this.removeDarkenLayer();
    }
    throttle(callback, throttleTime) {
        clearTimeout(this.timeoutId);
        // @ts-ignore
        this.timeoutId = setTimeout(() => {
            callback();
        }, throttleTime !== undefined ? throttleTime : this.context.pluginConfiguration.suggestions.throttleTime);
    }
    update() {
        const { suggestions } = this.context.pluginConfiguration;
        const minChars = suggestions.minChars;
        const triggersSearch = suggestions.triggersSearch;
        if (this.lastVal.length >= minChars && triggersSearch) {
            this.handleEnter(this.searchBox);
            return;
        }
        if (triggersSearch) {
            return;
        }
        this.renderPrevented = false;
        if (this.lastVal.length >= minChars) {
            this.loadAndRender();
        }
        else if (suggestions.showEmptyStateSuggestions) {
            this.state = State.Empty;
            this.showEmptyState();
        }
        else {
            this.hide();
        }
    }
    preSuggest() {
        const preSuggest = this.context.pluginConfiguration.callbacks.preSuggest;
        if (preSuggest !== undefined) {
            let keepGoing = true;
            try {
                keepGoing = preSuggest(this.lastVal, this.searchBox.get()[0]);
            }
            catch (ex) {
                Logger.warn(ex);
            }
            return keepGoing;
        }
        return true;
    }
    loadAndRender() {
        if (!this.preSuggest()) {
            return;
        }
        const source = this.context.pluginConfiguration.suggestions.source.queryBased;
        const query = this.lastVal;
        const loadStartTs = new Date().getTime();
        if (this.cache[query] !== undefined) {
            this.render(this.cache[this.lastVal], source, loadStartTs, query);
            this.state = State.QueryBased;
        }
        else {
            DataLoader.load(this.context, this.suggestUrl, query, source.content, (blocks) => {
                this.state = State.QueryBased;
                if (this.context.pluginConfiguration.suggestions.splitIntoClusters) {
                    const groupedEntries = {};
                    blocks.forEach((block) => {
                        block.dataSet.data.forEach((entry) => {
                            const contentGroup = entry.contentGroup || '_';
                            if (groupedEntries[contentGroup] === undefined) {
                                groupedEntries[contentGroup] = new SuggestionBlock({
                                    type: block.dataSet.type,
                                    attribution: block.dataSet.attribution,
                                    data: [],
                                    plan: block.dataSet.plan
                                }, block.layoutConfiguration, block.dataPoints, block.categoryStyle, block.config, block.style);
                            }
                            groupedEntries[contentGroup].dataSet.data.push(entry);
                        });
                    });
                    blocks = Object.keys(groupedEntries).map((key) => groupedEntries[key]);
                }
                if (this.context.pluginConfiguration.callbacks.suggestsLoaded !== undefined) {
                    try {
                        this.context.pluginConfiguration.callbacks.suggestsLoaded(blocks);
                    }
                    catch (ex) {
                        Logger.error(ex);
                    }
                }
                if (this.context.pluginConfiguration._cacheQueries) {
                    this.cache[query] = blocks;
                }
                if (query === this.lastVal) {
                    this.render(blocks, source, loadStartTs, query);
                }
            }, this.includeOverrides, this.excludeOverrides);
        }
    }
    // eslint-disable-next-line class-methods-use-this
    showEmptyState() {
        if (!this.preSuggest()) {
            return;
        }
        const source = this.context.pluginConfiguration.suggestions.source.emptyState;
        const loadStartTs = new Date().getTime();
        if (this.emptyCache !== undefined) {
            this.render(this.emptyCache, source, loadStartTs, this.lastVal);
        }
        else {
            DataLoader.load(this.context, this.suggestUrl, undefined, source.content, (blocks) => {
                this.emptyCache = blocks;
                this.render(blocks, source, loadStartTs, this.lastVal);
            }, this.includeOverrides, this.excludeOverrides);
        }
    }
    suggestSetHasChanged(blocks, source) {
        if (this.lastRenderedBlocks === undefined || this.lastRenderedSource === undefined) {
            return true;
        }
        const { lastRenderedBlocks, lastRenderedSource } = this;
        if (source.layoutType !== lastRenderedSource.layoutType || source.content.length !== lastRenderedSource.content.length) {
            return true;
        }
        for (let i = 0; i < source.content.length; i++) {
            const lastContent = lastRenderedSource.content[i];
            const content = source.content[i];
            if (lastContent !== content) {
                return true;
            }
        }
        if (lastRenderedBlocks.length !== blocks.length) {
            return true;
        }
        for (let i = 0; i < blocks.length; i++) {
            const lastBlockDataSet = lastRenderedBlocks[i].dataSet;
            const blockDataSet = blocks[i].dataSet;
            if (lastBlockDataSet.type !== blockDataSet.type) {
                return true;
            }
            if (lastBlockDataSet.data.length !== blockDataSet.data.length) {
                return true;
            }
            for (let j = 0; j < lastBlockDataSet.data.length; j++) {
                const lastEntry = lastBlockDataSet.data[j];
                const entry = blockDataSet.data[j];
                if (lastEntry.contentGroup !== entry.contentGroup) {
                    return true;
                }
                if (lastEntry.image !== entry.image) {
                    return true;
                }
                if (lastEntry.link !== entry.link) {
                    return true;
                }
                if (lastEntry.title !== entry.title) {
                    return true;
                }
                if (lastEntry.snippet !== entry.snippet) {
                    return true;
                }
                if ((lastEntry.dataPoints || []).length !== (entry.dataPoints || []).length) { // let's assume the data points are the same if everything else is the same (and the same number of data points)
                    return true;
                }
            }
        }
        return false;
    }
    hasNoSuggestsText(query) {
        const { noSuggestsText } = this.context.pluginConfiguration.suggestions;
        return noSuggestsText !== null && noSuggestsText !== undefined && noSuggestsText.length > 0 && (query === undefined || query.trim().length >= this.context.pluginConfiguration.suggestions.minChars);
    }
    appendNoSuggestsText(query) {
        this.suggestBox.find('.unibox__no-results-text').remove();
        this.suggestBox.append(`<span class="unibox__no-results-text">${this.context.pluginConfiguration.suggestions.noSuggestsText.replace(/#QUERY#/g, StringHelper.escapeHtml(query))}</span>`);
    }
    render(blocks, source, loadStartTs, query) {
        this.selectedEntryIndex = -1;
        if (this.renderPrevented) {
            return;
        }
        const suggestionsConfiguration = this.context.pluginConfiguration.suggestions;
        if (!this.suggestSetHasChanged(blocks, source)) {
            if (!this.isVisible && (this.totalLength > 0 || this.mode === UniboxMode.PopOver || this.hasNoSuggestsText(query))) {
                if (this.hasNoSuggestsText(query) && this.totalLength === 0) {
                    this.appendNoSuggestsText(query);
                }
                this.show();
            }
            else if (this.isVisible && this.totalLength === 0 && this.hasNoSuggestsText(query)) {
                this.appendNoSuggestsText(query);
            }
            if (this.totalLength > 0 && suggestionsConfiguration.highlight) {
                this.suggestBox.find('.unibox__selectable-snippet, .unibox__selectable-title').each((el) => {
                    const text = el.innerText || el.textContent || el.innerHTML; // in our Control Panel (and maybe with Vue Apps in general) innerText returns an empty string
                    el.innerHTML = (StringHelper.highlightSearchWords(StringHelper.escapeHtml(text), query));
                });
            }
            return;
        }
        this.lastRenderedBlocks = blocks;
        this.lastRenderedSource = source;
        this.suggestBox.find('.unibox__cluster, .unibox__selectable--show-all').remove();
        LAYOUT_TYPES.forEach((layoutType) => {
            const className = `unibox--layout-${layoutType}`;
            if (this.suggestBox.hasClass(className)) {
                this.suggestBox.removeClass(className);
            }
        });
        const layoutType = source.layoutType || 1;
        this.lastLayoutType = layoutType;
        this.suggestBox.addClass(`unibox--layout-${layoutType}`);
        this.blocks = [];
        let totalOffset = 0;
        const $blocks = [];
        const suggestTemplate = this.context.pluginConfiguration.suggestions.suggestTemplate;
        const suggestRenderer = suggestTemplate !== undefined && suggestTemplate.template !== undefined ? new TemplateRenderer(suggestTemplate) : undefined;
        const lColumn = layoutType === 3 ? sxQuery('<div class="unibox__column"></div>') : new SxQueryObject([]);
        const rColumn = layoutType === 3 ? sxQuery('<div class="unibox__column"></div>') : new SxQueryObject([]);
        blocks.forEach((block, idx) => {
            const blockOffset = totalOffset;
            totalOffset += block.dataSet.data.length;
            const $block = block.render(suggestionsConfiguration.highlight, this.lastVal, (entry, node, event) => {
                this.onItemSelected(entry, node, event);
            }, (button, resultGroup) => {
                this.onCtaClicked(button, resultGroup);
            }, this.context.pluginConfiguration.callbacks.suggestLine, (val) => {
                // @ts-ignore
                const historyEntries = this.context.readObject('unibox_search_history', []);
                const entryIdx = historyEntries.indexOf(StringHelper.unescapeHtml(val));
                if (entryIdx !== -1) {
                    historyEntries.splice(entryIdx, 1);
                    this.context.storeObject('unibox_search_history', historyEntries);
                }
                this.totalLength--;
                if (this.totalLength === 0 && this.mode === UniboxMode.Default) {
                    this.hide();
                }
            }, this.viewKeyMappings, (oldCount, newCount) => {
                this.preventFocusHandler = true;
                this.searchBox.focus();
                this.totalLength += (newCount - oldCount);
                const oldSelectedIndex = this.selectedEntryIndex;
                this.selectedEntryIndex = blockOffset;
                this.changeSelectedItem(oldSelectedIndex);
            }, this.context.pluginConfiguration.style.accentColor, () => {
                this.hide();
            }, suggestRenderer, blocks.length, suggestTemplate !== undefined ? suggestTemplate.includeContentGroups : undefined);
            $blocks.push($block);
            this.blocks.push(block);
            if (layoutType !== 3) {
                this.suggestBox.append($block);
            }
            else {
                (idx % 2 === 0 ? lColumn : rColumn).append($block);
            }
        });
        if (layoutType === 3) {
            this.suggestBox.append(lColumn);
            this.suggestBox.append(rColumn);
            const lBlocks = this.blocks.filter((b, idx) => idx % 2 === 0);
            const rBlocks = this.blocks.filter((b, idx) => idx % 2 !== 0);
            this.blocks = [];
            lBlocks.forEach((b) => this.blocks.push(b));
            rBlocks.forEach((b) => this.blocks.push(b));
        }
        this.totalLength = blocks.reduce((acc, block) => acc + block.length(), 0);
        const staticLayout = this.context.pluginConfiguration.suggestions.staticLayout;
        // if the left column is empty, and the right column isn't, then stretch the right column (to hide empty space on the left)
        if (this.totalLength > 0 && (layoutType === 2 || layoutType === 3)) {
            let rows = [];
            blocks.forEach((block, idx) => {
                const columnCount = block.getColumnCount(staticLayout);
                if (columnCount === 0) {
                    rows.push(0);
                }
                else if (columnCount === 1) {
                    rows.push(1);
                }
                else if (columnCount === 2) {
                    rows.push(1);
                    rows.push(1);
                }
                if (rows.length >= 2) {
                    if (rows.length === 2 && rows[0] === 0 && rows[1] === 1) {
                        $blocks[idx].addClass('unibox__cluster--layout-stretch');
                    }
                    rows = [];
                }
            });
        }
        this.showAllButton = undefined;
        const accessibility = this.context.pluginConfiguration.accessibility;
        let statusText = accessibility.srNoSuggestionsText;
        if (this.totalLength === 0 && this.mode === UniboxMode.Default && !this.hasNoSuggestsText(query)) {
            this.hide();
        }
        else {
            this.suggestBox.find('.unibox__no-results-text').remove();
            if (this.hasNoSuggestsText(query) && this.totalLength === 0) {
                this.appendNoSuggestsText(query);
            }
            const viewAllLabel = suggestionsConfiguration.viewAllLabel;
            if (viewAllLabel !== undefined && viewAllLabel !== null && this.totalLength > 0) {
                this.appendShowAllButton();
            }
            let showAttribution = false;
            // @ts-ignore
            if (this.context.pluginConfiguration.forceBranding === true || BRANDED_PLANS.indexOf(this.context.pluginConfiguration.forceBranding) !== -1) {
                showAttribution = true;
            }
            else {
                let plan;
                let attribution = false;
                for (let i = 0; i < blocks.length; i++) {
                    plan = blocks[i].dataSet.plan;
                    attribution = blocks[i].dataSet.attribution;
                    if (plan !== undefined && (plan !== Plan.Custom || attribution !== undefined)) {
                        break;
                    }
                }
                if (plan !== undefined) {
                    showAttribution = BRANDED_PLANS.indexOf(plan) !== -1 || (plan === Plan.Custom && attribution === true);
                }
            }
            if (showAttribution && !this.context.getIsZoovu()) {
                // eslint-disable-next-line max-len
                const $watermark = sxQuery(`<div class="unibox__watermark" style="grid-column:1/3;display:block!important;visibility:visible!important;z-index:1!important;"><a href="https://sitesearch360.com" target="_blank" rel="sponsored"><img role="presentation" style="max-width:100px!important;float:right;height:auto!important" src="${UiHelper.getLogoSrc(this.context.pluginConfiguration.allowCookies)}"></a></div>`);
                $watermark.find('a').attr('alt', '');
                $watermark.find('a').attr('aria-label', this.context.pluginConfiguration.results.watermarkLabel);
                this.suggestBox.find('.unibox__watermark').remove();
                this.suggestBox.append($watermark);
            }
            this.show();
            if (this.context.pluginConfiguration.callbacks.suggestPostRender !== undefined) {
                this.context.pluginConfiguration.callbacks.suggestPostRender();
            }
            if (this.context.hasInsights()) {
                const queryDuration = (new Date().getTime()) - loadStartTs;
                this.context.getInsights().trackShowSuggests(this.searchBox.get()[0], this.suggestBox.get()[0], this.suggestBox.find('.unibox__selectable').get()[0], query, this.totalLength, queryDuration, this.state === State.Empty);
            }
            statusText = (this.totalLength > 1 ? accessibility.srSuggestionsCountText : accessibility.srOneSuggestionText).split('#COUNT#').join(this.totalLength.toString());
        }
        const statusElement = sxQuery(`#${this.uniboxStatusMessageId}`);
        if (statusElement.text() !== statusText) {
            statusElement.text(statusText);
        }
        const suggestChangeCallback = this.context.pluginConfiguration.callbacks.suggestChange;
        if (suggestChangeCallback !== undefined) {
            suggestChangeCallback(this.totalLength > 0 || this.hasNoSuggestsText(query), blocks.map((block) => block.dataSet));
        }
    }
    appendShowAllButton() {
        const viewAllLabel = this.context.pluginConfiguration.suggestions.viewAllLabel;
        const buttonNode = DomElementBuilder.createElement({
            tag: 'button',
            classList: ['unibox__selectable', 'unibox__selectable--show-all', 'unibox-n-button'],
            attrs: {
                'aria-selected': 'false',
                type: 'button',
                role: 'option'
            },
            children: [DomElementBuilder.createElement({
                    tag: 'span',
                    classList: ['unibox__show-all-text'],
                    html: viewAllLabel
                }), DomElementBuilder.createElement({
                    tag: 'i',
                    classList: ['unibox__show-all-icon'],
                    children: [Icons.getSvgIconNode(Icons.MAGNIFIER, this.context.pluginConfiguration.style.themeColor, undefined, 16, 16)],
                    attrs: {
                        role: 'presentation'
                    }
                })]
        });
        this.showAllButton = sxQuery(buttonNode);
        this.showAllButton.on('click', () => {
            this.onCtaClicked(this.showAllButton);
        });
        this.suggestBox.append(this.showAllButton);
    }
    updateSuggestUrl(val) {
        this.suggestUrl = val;
    }
    getText() {
        // @ts-ignore
        return this.searchBox.val();
    }
    getOriginalSearchBox() {
        return this.searchBox;
    }
    addDarkenLayer() {
        if (!this.context.pluginConfiguration.suggestions.focusLayer) {
            return;
        }
        const animationSpeed = this.context.pluginConfiguration.suggestions.focusLayerStyle === FocusLayerStyle.Blur ? 0 : this.context.pluginConfiguration.style.animationSpeed;
        const id = this.context.uniboxDarkenLayerId;
        let darkenLayer = sxQuery(`#${id}`);
        if (darkenLayer.length === 0) {
            const zIndexTarget = this.mode === UniboxMode.PopOver ? this.suggestBox : sxQuery(this.searchBoxParent);
            zIndexTarget.css('z-index', '999999');
            darkenLayer = sxQuery(`<div role="presentation" id="${id}" class="unibox-focus-layer unibox-focus-layer--${this.context.pluginConfiguration.suggestions.focusLayerStyle}"></div>`);
            sxQuery(this.searchBoxParent).parent().append(darkenLayer);
        }
        if (!darkenLayer.hasClass('unibox-focus-layer--active')) {
            darkenLayer.fadeIn(animationSpeed, () => {
                darkenLayer.addClass('unibox-focus-layer--active');
            });
        }
    }
    removeDarkenLayer() {
        const animationSpeed = this.context.pluginConfiguration.suggestions.focusLayerStyle === FocusLayerStyle.Blur ? 0 : this.context.pluginConfiguration.style.animationSpeed;
        const id = this.context.uniboxDarkenLayerId;
        const darkenLayer = sxQuery(`#${id}`);
        darkenLayer.fadeOut(animationSpeed, () => {
            const zIndexTarget = this.mode === UniboxMode.PopOver ? this.suggestBox : sxQuery(this.searchBoxParent);
            zIndexTarget.css('z-index', null);
            darkenLayer.removeClass('unibox-focus-layer--active');
            darkenLayer.remove();
        });
    }
}
export default UniBox;
